import { Trash2 } from 'lucide-react';
import { createColumnHelper } from '@tanstack/react-table';

import { Button } from '@@/buttons';
import { Datatable } from '@@/datatables';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';

import { NamespaceSelect } from '../rbac/NamespaceSelect';

import type {
  AllowedSource,
  AllowedSourceScope,
  KubernetesRegistryTypeSpecificData,
} from './types';

const storageKey = 'k8s-allowed-sources-table';
const settingsStore = createPersistedStore(storageKey);

const columnHelper = createColumnHelper<AllowedSource>();

interface Props {
  allowedSources: AllowedSource[];
  onChange: (
    key: 'allowedSources',
    value: KubernetesRegistryTypeSpecificData['allowedSources']
  ) => void;
}

export function AllowedSourcesDatatable({ allowedSources, onChange }: Props) {
  const tableState = useTableState(settingsStore, storageKey);

  return (
    <Datatable
      title="Allowed sources"
      getRowId={(row) => row.registryUrlPrefix}
      dataset={allowedSources}
      columns={getColumns(handleScopeChange, handleNamespacesChange)}
      settingsManager={tableState}
      isRowSelectable={() => true}
      renderTableActions={(selectedRows) => (
        <TableActions selectedRows={selectedRows} onRemove={handleRemoveRows} />
      )}
      data-cy="k8s-allowed-sources-datatable"
      emptyContentLabel="No allowed sources configured"
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function handleRemoveRows(items: AllowedSource[]) {
    const prefixesToRemove = new Set(
      items.map((item) => item.registryUrlPrefix)
    );
    const newSources = allowedSources.filter(
      (source) => !prefixesToRemove.has(source.registryUrlPrefix)
    );
    onChange('allowedSources', newSources);
  }

  function handleScopeChange(index: number, scope: AllowedSourceScope) {
    const newSources = [...allowedSources];
    newSources[index] = {
      ...newSources[index],
      scope,
      namespaces: scope === 'global' ? [] : newSources[index].namespaces,
    };
    onChange('allowedSources', newSources);
  }

  function handleNamespacesChange(index: number, namespaces: string[]) {
    const newSources = [...allowedSources];
    newSources[index] = { ...newSources[index], namespaces };
    onChange('allowedSources', newSources);
  }
}

function TableActions({
  selectedRows,
  onRemove,
}: {
  selectedRows: AllowedSource[];
  onRemove: (items: AllowedSource[]) => void;
}) {
  return (
    <Button
      color="dangerlight"
      size="small"
      icon={Trash2}
      onClick={() => onRemove(selectedRows)}
      data-cy="remove-allowed-source-button"
      disabled={selectedRows.length === 0}
    >
      Remove
    </Button>
  );
}

function getColumns(
  handleScopeChange: (index: number, scope: AllowedSourceScope) => void,
  handleNamespacesChange: (index: number, namespaces: string[]) => void
) {
  return [
    columnHelper.accessor('registryUrlPrefix', {
      header: 'Registry URL prefix',
      cell: ({ row }) => (
        <span data-cy={`allowed-source-url-${row.index}`}>
          {row.original.registryUrlPrefix}
        </span>
      ),
    }),
    columnHelper.accessor('scope', {
      header: 'Scope',
      cell: ({ row }) => (
        <select
          className="form-control input-sm"
          value={row.original.scope}
          onChange={(e) =>
            handleScopeChange(row.index, e.target.value as AllowedSourceScope)
          }
          data-cy={`allowed-source-scope-${row.index}`}
        >
          <option value="global">Global</option>
          <option value="namespaces">Specific namespaces</option>
        </select>
      ),
    }),
    columnHelper.display({
      id: 'namespaces',
      header: 'Namespaces',
      cell: ({ row }) =>
        row.original.scope === 'namespaces' ? (
          <NamespaceSelect
            selectedNamespaces={row.original.namespaces}
            onChange={(namespaces) =>
              handleNamespacesChange(row.index, namespaces)
            }
            placeholder="e.g. default, production"
            data-cy={`allowed-source-namespaces-${row.index}`}
            namespaces={row.original.namespaces}
          />
        ) : (
          <span className="text-muted">All namespaces</span>
        ),
    }),
  ];
}
